import type { OperatorRigState } from "@common/utils/operatorRig";
import { FloorNames } from "@common/utils/terrain";
import { jointTransform, operatorFoot, transformRigPoint } from "@common/utils/operatorKinematics";

export interface GroundFoot { x: number; y: number; lift: number; planted: boolean; }
export interface OperatorGrounding { left: GroundFoot; right: GroundFoot; }
export const GROUND_ANCHOR_Y = 2.55;

export function createOperatorGrounding(): OperatorGrounding {
    const foot = (): GroundFoot => ({ x: 0, y: 0, lift: 0, planted: true });
    return { left: foot(), right: foot() };
}

/** Project the existing boot soles onto the ground, without modifying the rig.
 * These offsets stay in screen axes: the player's parent rotates with aim. */
export function updateOperatorGrounding(out: OperatorGrounding, state: OperatorRigState): void {
    const yaw = Math.atan2(Math.sin(state.rotation) / 0.54, Math.cos(state.rotation));
    const cy = Math.cos(yaw), sy = Math.sin(yaw);
    for (let i = 0; i < 2; i++) {
        const side = i === 0 ? -1 : 1, foot = i === 0 ? out.left : out.right;
        const stride = operatorFoot(state, side), center = stride.center;
        const sole = transformRigPoint(jointTransform(center, stride.yaw, stride.pitch, 0, { x: 0, y: 0, z: 0 }),
            { x: center.x + 0.20, y: center.y, z: center.z - 0.14 });
        const x = sole.x, y = sole.y;
        foot.x = (x * cy - y * sy) * 1.15;
        foot.y = (x * sy + y * cy) * 0.54 + GROUND_ANCHOR_Y;
        foot.lift = stride.lift;
        foot.planted = stride.planted;
    }
}

export interface GroundMaterial {
    tint: number;
    opacity: number;
    effect: "grass" | "sand" | "water" | undefined;
    effectTint: number;
    duration: number;
}
const solid: GroundMaterial = { tint: 0x252a2d, opacity: 0.25, effect: undefined, effectTint: 0xffffff, duration: 0 };
const grass: GroundMaterial = { tint: 0x203527, opacity: 0.30, effect: "grass", effectTint: 0x7b9640, duration: 230 };
const sand: GroundMaterial = { tint: 0x725333, opacity: 0.27, effect: "sand", effectTint: 0xac854e, duration: 360 };
const water: GroundMaterial = { tint: 0x125c66, opacity: 0.16, effect: "water", effectTint: 0xd9fff4, duration: 520 };
const wood: GroundMaterial = { tint: 0x3a3024, opacity: 0.28, effect: undefined, effectTint: 0xffffff, duration: 0 };

/** Reuse the already resolved gameplay floor; no additional terrain queries. */
export function operatorGroundMaterial(floor: FloorNames): GroundMaterial {
    switch (floor) {
        case FloorNames.Grass: return grass;
        case FloorNames.Sand: return sand;
        case FloorNames.Water: return water;
        case FloorNames.Wood:
        case FloorNames.Log: return wood;
        default: return solid;
    }
}

/** Keep a brief footfall fixed on the map as its parent actor continues moving. */
export function groundMarkOffset(mark: number, actor: number): number { return mark - actor; }

export interface GroundMark { x: number; y: number; at: number; planted: boolean; }

/** Each foot has one slot. A new landing replaces that slot; standing/turning
 * does not emit marks. Floor changes, teleports and stair changes clear it. */
export function updateGroundMark(mark: GroundMark, foot: GroundFoot, x: number, y: number,
    moving: number, now: number, enabled: boolean, reset: boolean): boolean {
    if (reset) { mark.at = -Infinity; mark.planted = foot.planted; }
    const landing = enabled && moving > 0.2 && foot.planted && !mark.planted && now - mark.at > 140;
    if (landing) { mark.x = x + foot.x; mark.y = y + foot.y; mark.at = now; }
    mark.planted = foot.planted;
    return landing;
}
